import { useEffect, useState } from 'react';

/**
 * The width below which the sidebar drops its text labels and shows icons only.
 *
 * Must stay in step with the `lg2` screen in `web/tailwind.config.js` — the
 * labels are hidden by `hidden lg2:inline`, and this hook decides when to
 * compensate with hover tooltips. `useNavCollapsed.test.ts` reads the Tailwind
 * config and fails if the two drift apart.
 */
export const NAV_COLLAPSE_BREAKPOINT_PX = 1100;

const QUERY = `(max-width: ${NAV_COLLAPSE_BREAKPOINT_PX - 0.02}px)`;

/**
 * True while the sidebar is in its icon-only state.
 *
 * Used to attach `title` tooltips ONLY when the label is actually hidden: an
 * icon with no text needs a hover label to be identifiable, but repeating a
 * label that is already on screen is noise, and screen readers would announce
 * the name twice (once from `aria-label`, once from `title`).
 *
 * Falls back to `false` where `matchMedia` is unavailable (jsdom by default), so
 * the expanded, label-visible rendering is what tests see unless they stub it.
 */
export function useNavCollapsed(): boolean {
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
    return window.matchMedia(QUERY).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mql = window.matchMedia(QUERY);
    const onChange = (e: MediaQueryListEvent): void => setCollapsed(e.matches);
    setCollapsed(mql.matches);
    // Safari < 14 only has addListener; both are present on everything we target,
    // but addEventListener is absent on some older MediaQueryList polyfills.
    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', onChange);
      return () => mql.removeEventListener('change', onChange);
    }
    mql.addListener(onChange);
    return () => mql.removeListener(onChange);
  }, []);

  return collapsed;
}
